import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import UserAvatar from "../UserAvatar";
import {
  BarChart3,
  Users,
  Award,
  DollarSign,
  TrendingUp,
  PieChart,
  Trophy,
  Send,
  Calendar,
  Filter,
  Loader2,
  AlertCircle,
  Download,
  RefreshCw,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from "recharts";
import analyticsService from "../../services/analyticsService";
import { useResponsive } from "../responsive";

// --- Helpers ---

const extractHexColor = (colorString) => {
  if (!colorString) return null;
  const match = colorString.match(/\[(.*?)\]/);
  return match ? match[1] : colorString;
};

const CHART_COLORS = [
  "#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
  "#ec4899", "#06b6d4", "#84cc16", "#f97316", "#6366f1",
];

const SLIDER_TICKS = [7, 30, 90, 365];

const getSliderMarkerStyle = (val) => {
  const min = 7;
  const max = 365;
  const percent = (val - min) / (max - min);
  return {
    left: `calc(${percent * 100}% + ${8 - percent * 16}px)`,
    transform: "translateX(-50%)",
  };
};

const getDateRange = (days) => {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const start = new Date(end);
  start.setDate(start.getDate() - days);
  return { start, end };
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatWeek = (dateStr) => {
  const d = new Date(dateStr);
  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - d.getDay());
  return weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// --- Main Component ---

const AnalyticsSection = () => {
  const { isMobile } = useResponsive();
  // Data state
  const [recognitions, setRecognitions] = useState([]);
  const [teams, setTeams] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [recognitionTypes, setRecognitionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Refresh state
  const [refreshKey, setRefreshKey] = useState(0);

  // Filter state
  const [rangeDays, setRangeDays] = useState(30);
  const [useCustomDate, setUseCustomDate] = useState(false);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [chartGranularity, setChartGranularity] = useState("daily");
  const [volumeChartMode, setVolumeChartMode] = useState("bar");
  const [typeChartMode, setTypeChartMode] = useState("donut");

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [recs, tms, bdgs, types] = await Promise.all([
          analyticsService.fetchAllRecognitions(),
          analyticsService.fetchAllTeams(),
          analyticsService.fetchAllBudgets(),
          analyticsService.fetchRecognitionTypes(),
        ]);
        setRecognitions(recs);
        setTeams(tms);
        setBudgets(bdgs);
        setRecognitionTypes(types);
      } catch (err) {
        setError(err.message || "Failed to load analytics data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refreshKey]);

  // --- Filtered recognitions ---
  const filteredRecognitions = useMemo(() => {
    let filtered = recognitions;

    // Date filter
    let start, end;
    if (useCustomDate) {
      start = customStart ? new Date(customStart) : null;
      end = customEnd ? new Date(customEnd + "T23:59:59") : null;
    } else {
      const range = getDateRange(rangeDays);
      start = range.start;
      end = range.end;
    }
    if (start) {
      filtered = filtered.filter((r) => new Date(r.timestamp) >= start);
    }
    if (end) {
      filtered = filtered.filter((r) => new Date(r.timestamp) <= end);
    }

    // Team filter
    if (selectedTeam !== "all") {
      filtered = filtered.filter((r) => String(r.team_id) === String(selectedTeam));
    }

    // Recognition type filter
    if (selectedType !== "all") {
      filtered = filtered.filter(
        (r) => r.recognition_type?.name === selectedType
      );
    }

    return filtered;
  }, [recognitions, rangeDays, useCustomDate, customStart, customEnd, selectedTeam, selectedType]);

  // --- Budget filtering by team ---
  const filteredBudgets = useMemo(() => {
    if (selectedTeam === "all") return budgets;
    return budgets.filter((b) => b.team_id === selectedTeam);
  }, [budgets, selectedTeam]);

  // --- KPI computations ---
  const kpis = useMemo(() => {
    const totalRecognitions = filteredRecognitions.length;

    const participantIds = new Set();
    filteredRecognitions.forEach((r) => {
      if (r.sender?.id) participantIds.add(r.sender.id);
      if (r.recipient?.id) participantIds.add(r.recipient.id);
    });
    const activeParticipants = participantIds.size;

    const pointsAwarded = filteredRecognitions
      .filter((r) => r.points_status === "approved" && r.points_amount > 0)
      .reduce((sum, r) => sum + (r.points_amount || 0), 0);

    const budgetSpent = filteredBudgets.reduce(
      (sum, b) => sum + (parseFloat(b.used_amount) || 0),
      0
    );
    const budgetAllocated = filteredBudgets.reduce(
      (sum, b) => sum + (parseFloat(b.amount) || 0),
      0
    );
    const budgetUsagePercent =
      budgetAllocated > 0 ? (budgetSpent / budgetAllocated) * 100 : 0;

    const withPoints = filteredRecognitions.filter(
      (r) => r.points_amount > 0
    );
    const avgPoints =
      withPoints.length > 0
        ? withPoints.reduce((sum, r) => sum + (r.points_amount || 0), 0) /
          withPoints.length
        : 0;

    return {
      totalRecognitions,
      activeParticipants,
      pointsAwarded,
      budgetSpent,
      budgetAllocated,
      budgetUsagePercent,
      avgPoints,
    };
  }, [filteredRecognitions, filteredBudgets]);

  // --- Chart data: recognition volume over time ---
  const volumeChartData = useMemo(() => {
    // Determine the date range
    let start, end;
    if (useCustomDate) {
      start = customStart ? new Date(customStart) : null;
      end = customEnd ? new Date(customEnd + "T23:59:59") : null;
    } else {
      const range = getDateRange(rangeDays);
      start = range.start;
      end = range.end;
    }

    const buckets = {};

    // Fill in all dates/weeks in the range so the chart is evenly spaced
    if (start && end) {
      if (chartGranularity === "weekly") {
        const cursor = new Date(start);
        cursor.setDate(cursor.getDate() - cursor.getDay()); // align to week start
        while (cursor <= end) {
          const label = cursor.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          const sortKey = cursor.toISOString().slice(0, 10);
          if (!buckets[label]) buckets[label] = { label, count: 0, points: 0, sortKey };
          cursor.setDate(cursor.getDate() + 7);
        }
      } else {
        const cursor = new Date(start);
        while (cursor <= end) {
          const label = cursor.toLocaleDateString("en-US", { month: "short", day: "numeric" });
          const sortKey = cursor.toISOString().slice(0, 10);
          if (!buckets[label]) buckets[label] = { label, count: 0, points: 0, sortKey };
          cursor.setDate(cursor.getDate() + 1);
        }
      }
    }

    // Populate with actual data
    filteredRecognitions.forEach((r) => {
      const d = new Date(r.timestamp);
      const key =
        chartGranularity === "weekly" ? formatWeek(r.timestamp) : formatDate(r.timestamp);
      const sortKey =
        chartGranularity === "weekly"
          ? (() => { const w = new Date(d); w.setDate(w.getDate() - w.getDay()); return w.toISOString().slice(0, 10); })()
          : new Date(r.timestamp).toISOString().slice(0, 10);
      if (!buckets[key]) buckets[key] = { label: key, count: 0, points: 0, sortKey };
      buckets[key].count++;
      buckets[key].points += Number(r.points_amount || 0);
    });

    return Object.values(buckets).sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey)
    );
  }, [filteredRecognitions, chartGranularity, rangeDays, useCustomDate, customStart, customEnd]);

  // --- Chart data: recognition type breakdown ---
  const typeChartData = useMemo(() => {
    const counts = {};
    filteredRecognitions.forEach((r) => {
      const typeName = r.recognition_type?.name || "Other";
      if (!counts[typeName]) {
        counts[typeName] = {
          name: typeName,
          value: 0,
          color:
            extractHexColor(r.recognition_type?.color_from) || "#8b5cf6",
        };
      }
      counts[typeName].value++;
    });
    return Object.values(counts).sort((a, b) => b.value - a.value);
  }, [filteredRecognitions]);

  // --- Stacked bar chart data: group by date + recognition type ---
  const stackedSeries = useMemo(() => {
    if (typeChartMode !== "stacked") return [];

    const typeNames = Array.from(
      new Set(
        filteredRecognitions.map((r) => r.recognition_type?.name || "Other")
      )
    );
    const byDate = {};

    filteredRecognitions.forEach((r) => {
      const d = new Date(r.timestamp);
      const key =
        chartGranularity === "weekly"
          ? formatWeek(r.timestamp)
          : formatDate(r.timestamp);
      const sortKey =
        chartGranularity === "weekly"
          ? new Date(d.setDate(d.getDate() - d.getDay())).toISOString()
          : new Date(r.timestamp).toISOString().slice(0, 10);
      const typeName = r.recognition_type?.name || "Other";

      if (!byDate[key]) {
        const row = { label: key, sortKey };
        typeNames.forEach((n) => (row[n] = 0));
        byDate[key] = row;
      }
      byDate[key][typeName] += 1;
    });

    return Object.values(byDate).sort((a, b) =>
      a.sortKey.localeCompare(b.sortKey)
    );
  }, [filteredRecognitions, chartGranularity, typeChartMode]);

  // --- Top recipients ---
  const topRecipients = useMemo(() => {
    const map = {};
    filteredRecognitions.forEach((r) => {
      const id = r.recipient?.id;
      if (!id) return;
      if (!map[id]) {
        map[id] = {
          name: r.recipient.full_name || r.recipient.email,
          avatar_url: r.recipient.avatar_url || r.recipient.avatar || null,
          count: 0,
          points: 0,
        };
      }
      map[id].count++;
      if (r.points_amount > 0 && r.points_status === "approved") {
        map[id].points += r.points_amount;
      }
    });
    return Object.values(map)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredRecognitions]);

  // --- Top senders ---
  const topSenders = useMemo(() => {
    const map = {};
    filteredRecognitions.forEach((r) => {
      const id = r.sender?.id;
      if (!id) return;
      if (!map[id]) {
        map[id] = {
          name: r.sender.full_name || r.sender.email,
          avatar_url: r.sender.avatar_url || r.sender.avatar || null,
          count: 0,
          points: 0,
        };
      }
      map[id].count++;
      if (r.points_amount > 0 && r.points_status === "approved") {
        map[id].points += r.points_amount;
      }
    });
    return Object.values(map)
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [filteredRecognitions]);

  // --- CSV Export ---
  const handleExportCsv = () => {
    if (filteredRecognitions.length === 0) return;

    const headers = [
      "Date",
      "Sender Name",
      "Sender Email",
      "Recipient Name",
      "Recipient Email",
      "Points",
      "Points Status",
      "Type",
      "Message",
    ];

    const rows = filteredRecognitions.map((r) => {
      const date = new Date(r.timestamp).toISOString();
      const sName = r.sender?.full_name || r.sender?.email || "";
      const sEmail = r.sender?.email || "";
      const rName = r.recipient?.full_name || r.recipient?.email || "";
      const rEmail = r.recipient?.email || "";
      const points = r.points_amount || 0;
      const status = r.points_status || "";
      const type = r.recognition_type?.name || "";
      const message = `"${(r.message || "").replace(/"/g, '""')}"`;

      return [date, sName, sEmail, rName, rEmail, points, status, type, message];
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `recognitions_export_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // --- Loading / Error states ---
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <Loader2 className="w-12 h-12 text-purple-500 animate-spin mb-4" />
        <p className="text-slate-500 text-sm">Loading analytics data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <p className="text-slate-700 font-medium mb-1">Failed to load analytics</p>
        <p className="text-slate-500 text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold text-slate-800">Analytics</h1>
          <p className="hidden sm:block text-sm text-slate-600 mt-1">
            Recognition activity, participation, and budget insights
          </p>
        </div>
        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          <button
            onClick={handleExportCsv}
            className="inline-flex items-center gap-1 sm:gap-2 p-2 sm:px-3 sm:py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm text-sm font-medium"
            title="Export CSV"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export CSV</span>
          </button>
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="inline-flex items-center gap-1 sm:gap-2 p-2 sm:px-3 sm:py-2 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 shadow-sm text-sm font-medium"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-4"
      >
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Filters</span>
        </div>
        <div className="space-y-4">
          {/* Date range slider */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label className="text-xs text-slate-500 font-medium">Date Range</label>
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomDate}
                  onChange={(e) => setUseCustomDate(e.target.checked)}
                  className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                Custom dates
              </label>
            </div>
            {!useCustomDate ? (
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="flex flex-col flex-grow">
                  <input
                    type="range"
                    min="7"
                    max="365"
                    step="1"
                    list="admin-day-notches"
                    value={rangeDays}
                    onChange={(e) => setRangeDays(Number(e.target.value))}
                    className="w-full accent-purple-600 cursor-pointer"
                  />
                  <datalist id="admin-day-notches">
                    {SLIDER_TICKS.map((day) => (
                      <option key={day} value={day} />
                    ))}
                  </datalist>
                  <div className="relative w-full h-4 text-[10px] text-slate-400 font-medium select-none">
                    {SLIDER_TICKS.map((day) => (
                      <div
                        key={day}
                        className="absolute flex flex-col items-center"
                        style={getSliderMarkerStyle(day)}
                      >
                        <span>{day}d</span>
                      </div>
                    ))}
                  </div>
                </div>
                <span className="text-sm font-semibold text-slate-800 w-16 text-right flex-shrink-0">
                  {rangeDays} days
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                  <label className="text-xs text-slate-500 font-medium">Start</label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div className="flex flex-col gap-1 min-w-0">
                  <label className="text-xs text-slate-500 font-medium">End</label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-3 py-1.5 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Team + Recognition type filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Team filter */}
            <div className="flex flex-col gap-1 min-w-0">
              <label className="text-xs text-slate-500 font-medium">Team</label>
              <select
                value={selectedTeam}
                onChange={(e) => setSelectedTeam(e.target.value)}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 truncate"
              >
                <option value="all">All teams</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Recognition type filter */}
            <div className="flex flex-col gap-1 min-w-0">
              <label className="text-xs text-slate-500 font-medium">Recognition Type</label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-3 py-1.5 border border-slate-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 truncate"
              >
                <option value="all">All types</option>
                {recognitionTypes.map((t) => (
                  <option key={t.id || t.name} value={t.name}>
                    {t.emoji || ""} {t.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </motion.div>

      {/* KPI Tiles */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4"
      >
        <KpiTile
          icon={Award}
          label="Total Recognitions"
          value={kpis.totalRecognitions}
          color="purple"
        />
        <KpiTile
          icon={Users}
          label="Active Participants"
          value={kpis.activeParticipants}
          color="blue"
        />
        <KpiTile
          icon={TrendingUp}
          label="Points Awarded"
          value={kpis.pointsAwarded.toLocaleString()}
          color="green"
        />
        <KpiTile
          icon={DollarSign}
          label="Budget Spent"
          value={`$${kpis.budgetSpent.toLocaleString()}`}
          color="yellow"
        />
        <KpiTile
          icon={BarChart3}
          label="Budget Allocated"
          value={`$${kpis.budgetAllocated.toLocaleString()}`}
          color="teal"
          progress={kpis.budgetUsagePercent}
        />
        <KpiTile
          icon={Award}
          label="Avg Points/Recognition"
          value={kpis.avgPoints.toFixed(1)}
          color="pink"
        />
      </motion.div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recognition volume over time */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
              <h3 className="font-semibold text-slate-800 text-sm sm:text-base truncate">
                Recognition Volume
              </h3>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <div className="flex gap-1">
                {["bar", "area"].map((m) => (
                  <button
                    key={m}
                    onClick={() => setVolumeChartMode(m)}
                    className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-medium capitalize transition-colors ${
                      volumeChartMode === m
                        ? "bg-purple-100 text-purple-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
              <div className="w-px h-4 bg-slate-200" />
              <div className="flex gap-1">
                {["daily", "weekly"].map((g) => (
                  <button
                    key={g}
                    onClick={() => setChartGranularity(g)}
                    className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-medium capitalize transition-colors ${
                      chartGranularity === g
                        ? "bg-purple-100 text-purple-700"
                        : "text-slate-500 hover:bg-slate-100"
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {volumeChartData.length > 0 ? (
            volumeChartMode === "bar" ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={volumeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
                    width={isMobile ? 28 : 40}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                  />
                  <Bar
                    dataKey="count"
                    name="Recognitions"
                    fill="#8b5cf6"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={volumeChartData}>
                  <defs>
                    <linearGradient id="adminGradCount" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="adminGradPoints" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: isMobile ? 9 : 11, fill: "#64748b" }}
                    width={isMobile ? 28 : 40}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                  />
                  <Legend />
                  <Area
                    type="monotone"
                    name="Count"
                    dataKey="count"
                    stroke="#8b5cf6"
                    fill="url(#adminGradCount)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    name="Points"
                    dataKey="points"
                    stroke="#3b82f6"
                    fill="url(#adminGradPoints)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )
          ) : (
            <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">
              No data for selected range
            </div>
          )}
        </motion.div>

        {/* Recognition type breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-6"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
            <div className="flex items-center gap-2 min-w-0">
              <PieChart className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
              <h3 className="font-semibold text-slate-800 text-sm sm:text-base truncate">
                Recognition Type Breakdown
              </h3>
            </div>
            <div className="flex gap-1">
              {["donut", "stacked"].map((m) => (
                <button
                  key={m}
                  onClick={() => setTypeChartMode(m)}
                  className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[11px] sm:text-xs font-medium capitalize transition-colors ${
                    typeChartMode === m
                      ? "bg-purple-100 text-purple-700"
                      : "text-slate-500 hover:bg-slate-100"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
          {typeChartMode === "donut" ? (
            typeChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPieChart>
                  <Pie
                    data={typeChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={isMobile ? 45 : 60}
                    outerRadius={isMobile ? 75 : 100}
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={
                      isMobile
                        ? false
                        : ({ name, percent }) =>
                            `${name} (${(percent * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {typeChartData.map((entry, idx) => (
                      <Cell
                        key={entry.name}
                        fill={entry.color || CHART_COLORS[idx % CHART_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      fontSize: "12px",
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: "12px" }} />
                </RechartsPieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">
                No data for selected range
              </div>
            )
          ) : stackedSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stackedSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#64748b" }}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "12px",
                  }}
                />
                <Legend wrapperStyle={{ fontSize: "12px" }} />
                {typeChartData.map((t, idx) => (
                  <Bar
                    key={t.name}
                    dataKey={t.name}
                    stackId="a"
                    fill={t.color || CHART_COLORS[idx % CHART_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[280px] text-slate-400 text-sm">
              No data for selected range
            </div>
          )}
        </motion.div>
      </div>

      {/* Leaderboard Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Recipients */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-6"
        >
          <div className="flex items-center gap-2 mb-3 sm:mb-4">
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
            <h3 className="font-semibold text-slate-800 text-sm sm:text-base">Top 10 Recipients</h3>
          </div>
          {topRecipients.length > 0 ? (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">
                        #
                      </th>
                      <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">
                        Name
                      </th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">
                        Received
                      </th>
                      <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">
                        Points
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {topRecipients.map((r, i) => (
                      <tr
                        key={i}
                        className="border-b border-slate-100 last:border-0"
                      >
                        <td className="py-2 px-2 text-slate-400 font-medium">
                          {i + 1}
                        </td>
                        <td className="py-2 px-2 text-slate-800 font-medium">
                          <div className="flex items-center gap-2">
                            <UserAvatar
                              src={r.avatar_url}
                              name={r.name}
                              className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                              textClassName="text-xs"
                            />
                            <span className="truncate">{r.name}</span>
                          </div>
                        </td>
                        <td className="py-2 px-2 text-right text-slate-600">
                          {r.count}
                        </td>
                        <td className="py-2 px-2 text-right text-green-600 font-medium">
                          {r.points.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile list */}
              <ul className="sm:hidden space-y-1.5">
                {topRecipients.map((r, i) => (
                  <li key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-slate-400 font-semibold w-5 text-right flex-shrink-0">{i + 1}</span>
                    <UserAvatar src={r.avatar_url} name={r.name} className="w-6 h-6 rounded-full object-cover flex-shrink-0" textClassName="text-[10px]" />
                    <span className="min-w-0 flex-1 truncate text-slate-800 font-medium">{r.name}</span>
                    <span className="text-slate-600 flex-shrink-0">{r.count}×</span>
                    <span className="text-green-600 font-medium flex-shrink-0 w-14 text-right">{r.points.toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">
              No data for selected range
            </p>
          )}
        </motion.div>

        {/* Top Senders */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 sm:p-6"
        >
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-slate-800">Top 10 Senders</h3>
          </div>
          {topSenders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">
                      #
                    </th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500">
                      Name
                    </th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">
                      Sent
                    </th>
                    <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500">
                      Points
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {topSenders.map((s, i) => (
                    <tr
                      key={i}
                      className="border-b border-slate-100 last:border-0"
                    >
                      <td className="py-2 px-2 text-slate-400 font-medium">
                        {i + 1}
                      </td>
                      <td className="py-2 px-2 text-slate-800 font-medium">
                        <div className="flex items-center gap-2">
                          <UserAvatar
                            src={s.avatar_url}
                            name={s.name}
                            className="w-6 h-6 rounded-full object-cover flex-shrink-0"
                            textClassName="text-xs"
                          />
                          <span className="truncate">{s.name}</span>
                        </div>
                      </td>
                      <td className="py-2 px-2 text-right text-slate-600">
                        {s.count}
                      </td>
                      <td className="py-2 px-2 text-right text-blue-600 font-medium">
                        {s.points.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center py-8">
              No data for selected range
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

// --- KPI Tile subcomponent ---

const COLOR_MAP = {
  purple: {
    bg: "from-purple-50 to-purple-100",
    border: "border-purple-200",
    text: "text-purple-600",
    label: "text-purple-700",
    bar: "bg-purple-500",
  },
  blue: {
    bg: "from-blue-50 to-blue-100",
    border: "border-blue-200",
    text: "text-blue-600",
    label: "text-blue-700",
    bar: "bg-blue-500",
  },
  green: {
    bg: "from-green-50 to-green-100",
    border: "border-green-200",
    text: "text-green-600",
    label: "text-green-700",
    bar: "bg-green-500",
  },
  yellow: {
    bg: "from-yellow-50 to-yellow-100",
    border: "border-yellow-200",
    text: "text-yellow-600",
    label: "text-yellow-700",
    bar: "bg-yellow-500",
  },
  indigo: {
    bg: "from-indigo-50 to-indigo-100",
    border: "border-indigo-200",
    text: "text-indigo-600",
    label: "text-indigo-700",
    bar: "bg-indigo-500",
  },
  teal: {
    bg: "from-teal-50 to-teal-100",
    border: "border-teal-200",
    text: "text-teal-600",
    label: "text-teal-700",
    bar: "bg-teal-500",
  },
  pink: {
    bg: "from-pink-50 to-pink-100",
    border: "border-pink-200",
    text: "text-pink-600",
    label: "text-pink-700",
    bar: "bg-pink-500",
  },
};

const KpiTile = ({ icon: Icon, label, value, color, progress }) => {
  const c = COLOR_MAP[color] || COLOR_MAP.purple;
  return (
    <div
      className={`p-2 sm:p-4 bg-gradient-to-br ${c.bg} rounded-2xl border ${c.border} min-w-0`}
    >
      <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
        <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${c.text} flex-shrink-0`} />
        <p className={`text-[10px] sm:text-xs font-medium ${c.label} truncate`}>{label}</p>
      </div>
      <p className={`text-base sm:text-2xl font-bold ${c.text} truncate`}>{value}</p>
      {progress !== undefined && (
        <div className="mt-1.5 sm:mt-2">
          <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
            <div
              className={`h-full ${c.bar} rounded-full transition-all duration-300`}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
          <p className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 ${c.label}`}>{progress.toFixed(1)}% used</p>
        </div>
      )}
    </div>
  );
};

export default AnalyticsSection;
